import React, {useState, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CreateServiceModal from './CreateServiceModal';
import './ProjectResourcesTree.css';

/**
 * ProjectResourcesTree Component
 * 
 * Displays all project resources in a tree structure:
 * - Microservices
 * - Endpoints
 * - CRUD Collections
 * - Fast Data
 * - Flow Manager
 * - Infrastructure Resources
 */
const ProjectResourcesTree = ({
  configuration,
  loading,
  error,
  onResourceClick,
  onResourceDelete,
  onCreateService,
  selectedResource,
  collectionsOverride,
  showOnlyCollections = false,
  projectId,
  revisionId,
  tenantId,
  showCreateServiceModal,
  setShowCreateServiceModal
}) => {
  const [expandedSections, setExpandedSections] = useState({
    sources: true,
    containers: true,
    endpoints: true,
    collections: true,
    fastData: true,
    flowManager: true,
    infrastructure: true
  });
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle right-click context menu
  const handleContextMenu = (event, resourceType, resourceName, resourceData) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Get the click position
    const x = event.clientX;
    const y = event.clientY;
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Estimated context menu dimensions (will be adjusted after render)
    const menuWidth = 160;
    const menuHeight = 50;
    
    // Calculate position ensuring menu stays within viewport
    let finalX = x;
    let finalY = y;
    
    // Adjust horizontal position if menu would overflow
    if (x + menuWidth > viewportWidth) {
      finalX = viewportWidth - menuWidth - 10; // 10px padding from edge
    }
    
    // Adjust vertical position if menu would overflow
    if (y + menuHeight > viewportHeight) {
      finalY = viewportHeight - menuHeight - 10; // 10px padding from edge
    }
    
    // Ensure minimum distance from edges
    finalX = Math.max(10, finalX);
    finalY = Math.max(10, finalY);
    
    setContextMenu({
      x: finalX,
      y: finalY,
      resourceType,
      resourceName,
      resourceData
    });
  };

  // Handle resource deletion
  const handleDeleteResource = () => {
    if (!contextMenu || !onResourceDelete) {
      setContextMenu(null);
      return;
    }

    const {resourceType, resourceName, resourceData} = contextMenu;
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${resourceName}"?\n\nThis will mark the resource for deletion in the project configuration.`
    );
    
    if (confirmDelete) {
      onResourceDelete(resourceType, resourceName, resourceData);
      // eslint-disable-next-line no-console
      console.log('🗑️ Marked resource for deletion:', resourceType, resourceName);
    }
    
    setContextMenu(null);
  };

  // Extract resources from configuration
  // All resources come as objects with keys being the resource names
  const allMicroservices = configuration?.services ? Object.entries(configuration.services) : [];
  
  // Split microservices into Sources (with repoUrl), Containers (without), and Infrastructure (custom-resource type)
  const sources = allMicroservices.filter(([, service]) => 
    service.type !== 'custom-resource' && (service.repositoryUrl || service.repoUrl || service.sshUrl)
  );
  const containers = allMicroservices.filter(([, service]) => 
    service.type !== 'custom-resource' && !service.repositoryUrl && !service.repoUrl && !service.sshUrl
  );
  const infrastructureResources = allMicroservices.filter(([, service]) => 
    service.type === 'custom-resource'
  );
  
  const endpoints = configuration?.endpoints ? Object.entries(configuration.endpoints) : [];
  // Use collectionsOverride if provided, otherwise fall back to configuration.collections
  const collections = collectionsOverride ? Object.entries(collectionsOverride) : 
                      (configuration?.collections ? Object.entries(configuration.collections) : []);
  
  // Fast Data can be in fastDataConfig or fastData
  const fastDataConfig = configuration?.fastDataConfig || configuration?.fastData || configuration?.['fast-data'];
  
  // Check if fastDataConfig has actual content (not just a skeleton)
  const isFastDataEmpty = (config) => {
    if (!config) return true;
    
    // Check if there are systems with actual projections or single views
    const hasSystemsContent = config.systems && 
      typeof config.systems === 'object' && 
      Object.keys(config.systems).length > 0 &&
      Object.values(config.systems).some(system => {
        const hasProjections = system.projections && Object.keys(system.projections).length > 0;
        const hasSingleViews = system.singleViews && Object.keys(system.singleViews).length > 0;
        return hasProjections || hasSingleViews;
      });
    
    // Check if there are top-level single views
    const hasTopLevelSingleViews = config.singleViews && 
      typeof config.singleViews === 'object' && 
      Object.keys(config.singleViews).length > 0;
    
    // Check if there are ER schemas
    const hasErSchemas = config.erSchemas && 
      typeof config.erSchemas === 'object' && 
      Object.keys(config.erSchemas).length > 0;
    
    // FastData is not empty if it has any of these
    return !hasSystemsContent && !hasTopLevelSingleViews && !hasErSchemas;
  };
  
  const fastData = (fastDataConfig && !isFastDataEmpty(fastDataConfig)) ? [fastDataConfig] : []; // Fast Data config is a single object
  
  // Flow Manager configurations - can be in microfrontendPluginsConfig.flowManagerConfigurations or flowManagerConfigurations
  const flowManagerConfig = configuration?.microfrontendPluginsConfig?.flowManagerConfigurations || 
                            configuration?.flowManagerConfigurations;
  
  // Extract individual flows from the flowManagerConfig
  const flowManager = flowManagerConfig?.flows ? Object.entries(flowManagerConfig.flows) : [];
  
  const customInfrastructure = configuration?.infrastructureResources ? Object.entries(configuration.infrastructureResources) : 
                               configuration?.customResources ? Object.entries(configuration.customResources) : [];

  // Debug logging - Disabled to prevent console spam on every render
  // eslint-disable-next-line no-console
  // console.log('📦 [ProjectResourcesTree] Resource counts:', {
  //   sources: sources.length,
  //   containers: containers.length,
  //   endpoints: endpoints.length,
  //   collections: collections.length,
  //   fastData: fastData.length,
  //   flowManager: flowManager.length,
  //   infrastructure: infrastructureResources.length,
  //   customInfrastructure: customInfrastructure.length
  // });

  // Count total resources
  const counts = {
    sources: sources.length,
    containers: containers.length,
    endpoints: endpoints.length,
    collections: collections.length,
    fastData: fastData.length,
    flowManager: flowManager.length,
    infrastructure: infrastructureResources.length,
    customInfrastructure: customInfrastructure.length
  };

  const totalResources = Object.values(counts).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="resources-tree-loading">
        <div className="loading-spinner"></div>
        <span>{'Loading resources...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resources-tree-error">
        <span className="error-icon">{'⚠️'}</span>
        <span>{error}</span>
      </div>
    );
  }

  // Always render the tree structure, even when empty
  return (
    <div className="project-resources-tree">
      {/* Sources Section - Microservices with repository */}
      {!showOnlyCollections && (
        <div className="resource-section">
          <div className="resource-section-header-wrapper">
            <button
              className="resource-section-header"
              onClick={() => toggleSection('sources')}
              type="button"
            >
              <span className="section-icon">{expandedSections.sources ? '▼' : '▶'}</span>
              <span className="section-icon-type">{'💻'}</span>
              <span className="section-title">{'My Code'}</span>
              <span className="section-count">{counts.sources}</span>
            </button>
            <button
              className="add-resource-btn-inline"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateServiceModal(true);
              }}
              title="Add new code template"
              type="button"
            >
              {'+'}
            </button>
          </div>
          {expandedSections.sources && (
            counts.sources > 0 ? (
              <div className="resource-list">
                {sources.map(([name, service]) => (
                  <button
                    className={`resource-item ${selectedResource?.type === 'service' && selectedResource?.name === name ? 'selected' : ''}`}
                    key={name}
                    onClick={() => onResourceClick({type: 'service', name, data: service})}
                    onContextMenu={(e) => handleContextMenu(e, 'service', name, service)}
                    type="button"
                  >
                    <span className="resource-icon">{'📄'}</span>
                    <span className="resource-name">{name}</span>
                    {service.tags && service.tags.length > 0 && (
                      <span className={`resource-badge badge-${service.tags[0]}`}>
                        {service.tags[0]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state-inline">No code templates yet. Click + to add.</div>
            )
          )}
        </div>
      )}

      {/* Containers Section - Microservices without repository */}
      {!showOnlyCollections && (
        <div className="resource-section">
          <div className="resource-section-header-wrapper">
            <button
              className="resource-section-header"
              onClick={() => toggleSection('containers')}
              type="button"
            >
              <span className="section-icon">{expandedSections.containers ? '▼' : '▶'}</span>
              <span className="section-icon-type">{'🚀'}</span>
              <span className="section-title">{'Services'}</span>
              <span className="section-count">{counts.containers}</span>
            </button>
            <button
              className="add-resource-btn-inline"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateServiceModal(true);
              }}
              title="Add new service"
              type="button"
            >
              {'+'}
            </button>
          </div>
          {expandedSections.containers && (
            counts.containers > 0 ? (
              <div className="resource-list">
                {containers.map(([name, service]) => (
                  <button
                    className={`resource-item ${selectedResource?.type === 'service' && selectedResource?.name === name ? 'selected' : ''}`}
                    key={name}
                    onClick={() => onResourceClick({type: 'service', name, data: service})}
                    onContextMenu={(e) => handleContextMenu(e, 'service', name, service)}
                    type="button"
                  >
                    <span className="resource-icon">{'🌐'}</span>
                    <span className="resource-name">{name}</span>
                    {service.tags && service.tags.length > 0 && (
                      <span className={`resource-badge badge-${service.tags[0]}`}>
                        {service.tags[0].toUpperCase()}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state-inline">No services yet. Click + to add.</div>
            )
          )}
        </div>
      )}

      {/* Endpoints Section */}
      {!showOnlyCollections && (
        <div className="resource-section">
          <div className="resource-section-header-wrapper">
            <button
              className="resource-section-header"
              onClick={() => toggleSection('endpoints')}
              type="button"
            >
              <span className="section-icon">{expandedSections.endpoints ? '▼' : '▶'}</span>
              <span className="section-icon-type">{'🔌'}</span>
              <span className="section-title">{'API Endpoints'}</span>
              <span className="section-count">{counts.endpoints}</span>
            </button>
            <button
              className="add-resource-btn-inline"
              onClick={(e) => {
                e.stopPropagation();
                // Future: Endpoint creation modal
              }}
              title="Add API endpoint"
              type="button"
            >
              {'+'}
            </button>
          </div>
          {expandedSections.endpoints && (
            counts.endpoints > 0 ? (
              <div className="resource-list">
                {endpoints.map(([name, endpoint]) => (
                  <button
                    className={`resource-item ${selectedResource?.type === 'endpoint' && selectedResource?.name === name ? 'selected' : ''}`}
                    key={name}
                    onClick={() => onResourceClick({type: 'endpoint', name, data: endpoint})}
                    onContextMenu={(e) => handleContextMenu(e, 'endpoint', name, endpoint)}
                    type="button"
                  >
                    <span className="resource-icon">{'🌐'}</span>
                    <span className="resource-name">{name}</span>
                    {endpoint.type && (
                      <span className={`resource-badge badge-${endpoint.type}`}>
                        {endpoint.type}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state-inline">No endpoints yet. Click + to add.</div>
            )
          )}
        </div>
      )}

      {/* CRUD Collections Section */}
      <div className="resource-section">
        <div className="resource-section-header-wrapper">
          <button
            className="resource-section-header"
            onClick={() => toggleSection('collections')}
            type="button"
          >
            <span className="section-icon">{expandedSections.collections ? '▼' : '▶'}</span>
            <span className="section-icon-type">{'🗄️'}</span>
            <span className="section-title">{'Database'}</span>
            <span className="section-count">{counts.collections}</span>
          </button>
          <button
            className="add-resource-btn-inline"
            onClick={(e) => {
              e.stopPropagation();
              // Future: Collection creation modal
            }}
            title="Add database collection"
            type="button"
          >
            {'+'}
          </button>
        </div>
        {expandedSections.collections && (
          counts.collections > 0 ? (
            <div className="resource-list">
              {collections.map(([name, collection]) => (
                <button
                  className={`resource-item ${selectedResource?.type === 'collection' && selectedResource?.name === name ? 'selected' : ''}`}
                  key={name}
                  onClick={() => onResourceClick({type: 'collection', name, data: collection})}
                  onContextMenu={(e) => handleContextMenu(e, 'collection', name, collection)}
                  type="button"
                >
                  <span className="resource-icon">{'📊'}</span>
                  <span className="resource-name">{name}</span>
                  {collection.type && (
                    <span className="resource-badge badge-crud">
                      {collection.type}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="empty-state-inline">No collections yet. Click + to add.</div>
          )
        )}
      </div>

      {/* Fast Data Section */}
      {!showOnlyCollections && counts.fastData > 0 && (
        <div className="resource-section">
          <div className="resource-section-header-wrapper">
            <button
              className="resource-section-header"
              onClick={() => toggleSection('fastData')}
              type="button"
            >
              <span className="section-icon">{expandedSections.fastData ? '▼' : '▶'}</span>
              <span className="section-icon-type">{'⚡'}</span>
              <span className="section-title">{'Fast Data'}</span>
              <span className="section-count">{counts.fastData}</span>
            </button>
            <button
              className="add-resource-btn-inline"
              onClick={(e) => {
                e.stopPropagation();
                // Future: Fast Data creation
              }}
              title="Create Fast Data system"
              type="button"
            >
              {'+'}
            </button>
          </div>
          {expandedSections.fastData && (
            counts.fastData > 0 ? (
              <div className="resource-list">
                {fastData.map((item, idx) => {
                // Check if this Fast Data has systems (grouped by typology)
                const hasSystems = item.systems && Object.keys(item.systems).length > 0;
                
                if (hasSystems) {
                  // Render grouped by systems
                  return (
                    <div className="fast-data-systems-container" key={idx}>
                      {Object.entries(item.systems).map(([systemId, system]) => {
                        const systemExpandedKey = `fastData_system_${systemId}`;
                        const isSystemExpanded = expandedSections[systemExpandedKey] ?? true;
                        
                        // Count projections and single views
                        const projectionCount = system.projections ? Object.keys(system.projections).length : 0;
                        const singleViewCount = system.singleViews ? Object.keys(system.singleViews).length : 0;
                        const totalItems = projectionCount + singleViewCount;
                        
                        return (
                          <div className="fast-data-system-group" key={systemId}>
                            <button
                              className="fast-data-system-header"
                              onClick={() => toggleSection(systemExpandedKey)}
                              type="button"
                            >
                              <span className="section-icon">{isSystemExpanded ? '▼' : '▶'}</span>
                              <span className="resource-icon">{'📦'}</span>
                              <span className="resource-name">{systemId}</span>
                              <span className="resource-badge badge-fastdata">
                                {`${totalItems} items`}
                              </span>
                            </button>
                            
                            {isSystemExpanded && (
                              <div className="fast-data-system-content">
                                {/* Projections */}
                                {system.projections && Object.entries(system.projections).map(([projName, projection]) => (
                                  <button
                                    className={`resource-item resource-item-nested ${
                                      selectedResource?.type === 'fastDataProjection' && 
                                      selectedResource?.name === projName ? 'selected' : ''
                                    }`}
                                    key={projName}
                                    onClick={() => onResourceClick({
                                      type: 'fastDataProjection', 
                                      name: projName, 
                                      data: projection,
                                      system: systemId
                                    })}
                                    type="button"
                                  >
                                    <span className="resource-icon">{'📊'}</span>
                                    <span className="resource-name">{projName}</span>
                                    <span className="resource-badge badge-projection">
                                      {'projection'}
                                    </span>
                                  </button>
                                ))}
                                
                                {/* Single Views */}
                                {system.singleViews && Object.entries(system.singleViews).map(([svName, singleView]) => (
                                  <button
                                    className={`resource-item resource-item-nested ${
                                      selectedResource?.type === 'fastDataSingleView' && 
                                      selectedResource?.name === svName ? 'selected' : ''
                                    }`}
                                    key={svName}
                                    onClick={() => onResourceClick({
                                      type: 'fastDataSingleView', 
                                      name: svName, 
                                      data: singleView,
                                      system: systemId
                                    })}
                                    type="button"
                                  >
                                    <span className="resource-icon">{'👁️'}</span>
                                    <span className="resource-name">{svName}</span>
                                    <span className="resource-badge badge-singleview">
                                      {'single view'}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else {
                  // Fallback to original rendering if no systems
                  const itemName = 'Fast Data Config';
                  return (
                    <button
                      className={`resource-item ${selectedResource?.type === 'fastData' && selectedResource?.name === itemName ? 'selected' : ''}`}
                      key={idx}
                      onClick={() => onResourceClick({type: 'fastData', name: itemName, data: item})}
                      type="button"
                    >
                      <span className="resource-icon">{'💨'}</span>
                      <span className="resource-name">{itemName}</span>
                      {item.version && (
                        <span className="resource-badge badge-fastdata">
                          {`v${item.version}`}
                        </span>
                      )}
                    </button>
                  );
                }
              })}
            </div>
            ) : (
              <div className="empty-state-inline">No Fast Data yet. Click + to create.</div>
            )
          )}
        </div>
      )}

      {/* Flow Manager Section */}
      {!showOnlyCollections && counts.flowManager > 0 && (
        <div className="resource-section">
          <button
            className="resource-section-header"
            onClick={() => toggleSection('flowManager')}
            type="button"
          >
            <span className="section-icon">{expandedSections.flowManager ? '▼' : '▶'}</span>
            <span className="section-icon-type">{'🔄'}</span>
            <span className="section-title">{'Flow Manager'}</span>
            <span className="section-count">{counts.flowManager}</span>
          </button>
          {expandedSections.flowManager && (
            <div className="resource-list">
              {flowManager.map(([flowName, flowData]) => {
                const stateCount = flowData.states ? Object.keys(flowData.states).length : 0;
                const eventCount = flowData.events ? Object.keys(flowData.events).length : 0;
                return (
                  <button
                    className={`resource-item ${selectedResource?.type === 'flowManager' && selectedResource?.name === flowName ? 'selected' : ''}`}
                    key={flowName}
                    onClick={() => onResourceClick({type: 'flowManager', name: flowName, data: flowData})}
                    type="button"
                  >
                    <span className="resource-icon">{'📈'}</span>
                    <span className="resource-name">{flowName}</span>
                    <span className="resource-badge badge-flow">
                      {`${stateCount} states, ${eventCount} events`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Infrastructure Section - Custom Resources from Services */}
      {!showOnlyCollections && (
        <div className="resource-section">
          <div className="resource-section-header-wrapper">
            <button
              className="resource-section-header"
              onClick={() => toggleSection('infrastructure')}
              type="button"
            >
              <span className="section-icon">{expandedSections.infrastructure ? '▼' : '▶'}</span>
              <span className="section-icon-type">{'🏗️'}</span>
              <span className="section-title">{'Resources'}</span>
              <span className="section-count">{counts.infrastructure}</span>
            </button>
            <button
              className="add-resource-btn-inline"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateServiceModal(true);
              }}
              title="Add infrastructure resource"
              type="button"
            >
              {'+'}
            </button>
          </div>
          {expandedSections.infrastructure && (
            counts.infrastructure > 0 ? (
              <div className="resource-list">
                {infrastructureResources.map(([name, resource]) => (
                  <button
                    className={`resource-item ${selectedResource?.type === 'infrastructure' && selectedResource?.name === name ? 'selected' : ''}`}
                    key={name}
                    onClick={() => onResourceClick({type: 'infrastructure', name, data: resource})}
                    onContextMenu={(e) => handleContextMenu(e, 'infrastructure', name, resource)}
                    type="button"
                  >
                    <span className="resource-icon">{'🔩'}</span>
                    <span className="resource-name">{name}</span>
                    {resource.meta?.kind && (
                      <span className="resource-badge badge-infrastructure">
                        {resource.meta.kind}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-state-inline">No infrastructure resources yet. Click + to add.</div>
            )
          )}
        </div>
      )}

      {/* Custom Infrastructure Resources Section */}
      {!showOnlyCollections && counts.customInfrastructure > 0 && (
        <div className="resource-section">
          <button
            className="resource-section-header"
            onClick={() => toggleSection('customInfrastructure')}
            type="button"
          >
            <span className="section-icon">{expandedSections.customInfrastructure ? '▼' : '▶'}</span>
            <span className="section-icon-type">{'⚙️'}</span>
            <span className="section-title">{'Custom Resources'}</span>
            <span className="section-count">{counts.customInfrastructure}</span>
          </button>
          {expandedSections.customInfrastructure && (
            <div className="resource-list">
              {customInfrastructure.map(([name, resource]) => (
                <button
                  className={`resource-item ${selectedResource?.type === 'customInfrastructure' && selectedResource?.name === name ? 'selected' : ''}`}
                  key={name}
                  onClick={() => onResourceClick({type: 'customInfrastructure', name, data: resource})}
                  onContextMenu={(e) => handleContextMenu(e, 'customInfrastructure', name, resource)}
                  type="button"
                >
                  <span className="resource-icon">{'🔧'}</span>
                  <span className="resource-name">{name}</span>
                  {resource.type && (
                    <span className="resource-badge badge-infrastructure">
                      {resource.type}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Context Menu - Rendered via Portal to escape transform context */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="context-menu"
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 9999
          }}
        >
          <button
            className="context-menu-item delete"
            onClick={handleDeleteResource}
            type="button"
          >
            <span>{'🗑️'}</span>
            <span>{'Delete Resource'}</span>
          </button>
        </div>,
        document.body
      )}

      {/* Create Service Modal */}
      {showCreateServiceModal && (
        <CreateServiceModal
          isOpen={showCreateServiceModal}
          onClose={() => setShowCreateServiceModal(false)}
          onCreate={onCreateService}
          projectId={projectId}
          revisionId={revisionId}
          tenantId={tenantId}
        />
      )}
    </div>
  );
};

ProjectResourcesTree.propTypes = {
  collectionsOverride: PropTypes.object,
  configuration: PropTypes.object,
  error: PropTypes.string,
  loading: PropTypes.bool,
  onCreateService: PropTypes.func,
  onResourceClick: PropTypes.func.isRequired,
  onResourceDelete: PropTypes.func,
  projectId: PropTypes.string,
  revisionId: PropTypes.string,
  selectedResource: PropTypes.object,
  setShowCreateServiceModal: PropTypes.func,
  showCreateServiceModal: PropTypes.bool,
  showOnlyCollections: PropTypes.bool,
  tenantId: PropTypes.string
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(ProjectResourcesTree);
